import React from "react";
import ReactDOM from "react-dom/client";
import axios from "axios";
import "@/index.css";
import App from "@/App";
import ErrorBoundary from "@/components/ErrorBoundary";

// ============================================================================
// GLOBAL AXIOS INTERCEPTOR - Prevents duplicate mutation requests
// ============================================================================
// This interceptor blocks duplicate POST/PUT/PATCH/DELETE requests to the same
// URL while a previous request is still in flight. This prevents double-clicks
// and network retries from creating duplicate transactions.
// ============================================================================

const pendingRequests = new Map();

// Generate a unique key for each request
const getRequestKey = (config) => {
  const { method, url, data } = config;
  
  // For FormData (file uploads), skip body in the key — use URL + method only
  // FormData cannot be reliably stringified
  if (data instanceof FormData) {
    return `${method}:${url}:formdata`;
  }
  
  // For JSON data, include body hash in the key
  const bodyHash = data ? JSON.stringify(data) : '';
  return `${method}:${url}:${bodyHash}`;
};

// REQUEST INTERCEPTOR - Block duplicates while request is in flight
axios.interceptors.request.use(
  (config) => {
    // Only guard mutations (POST, PUT, PATCH, DELETE)
    const method = config.method?.toLowerCase();
    if (!['post', 'put', 'patch', 'delete'].includes(method)) {
      return config;
    }
    
    const key = getRequestKey(config);
    
    // PRIMARY CHECK: Is there a request in flight to the same endpoint with same data?
    if (pendingRequests.has(key)) {
      console.warn(`[API Guard] BLOCKED: Duplicate ${method.toUpperCase()} to ${config.url} — previous request still pending`);
      const cancelError = new axios.CancelToken.source();
      cancelError.cancel('[API Guard] Duplicate request blocked - previous request pending');
      config.cancelToken = cancelError.token;
      return config;
    }
    
    // Track this request
    pendingRequests.set(key, { startedAt: Date.now() });
    config._requestKey = key;
    
    return config;
  },
  (error) => Promise.reject(error)
);

// RESPONSE INTERCEPTOR - Remove from pending on completion (success or error)
axios.interceptors.response.use(
  (response) => {
    if (response.config._requestKey) {
      pendingRequests.delete(response.config._requestKey);
    }
    return response;
  },
  (error) => {
    // Remove from pending on error (except for cancelled requests)
    if (error.config?._requestKey && !axios.isCancel(error)) {
      pendingRequests.delete(error.config._requestKey);
    }
    return Promise.reject(error);
  }
);

// FALLBACK CLEANUP: Remove hung requests after 60 seconds
// This handles edge cases where response never arrives
setInterval(() => {
  const now = Date.now();
  for (const [key, { startedAt }] of pendingRequests.entries()) {
    if (now - startedAt > 60000) {
      console.warn(`[API Guard] CLEANUP: Removing hung request (60s timeout): ${key.substring(0, 100)}...`);
      pendingRequests.delete(key);
    }
  }
}, 10000);

// ============================================================================
// END AXIOS INTERCEPTOR
// ============================================================================

// Suppress ResizeObserver loop errors globally - these are non-critical browser warnings
// that can occur when layouts resize during animations/transitions
// This patches the ResizeObserver constructor to catch the error before it propagates
const OriginalResizeObserver = window.ResizeObserver;
window.ResizeObserver = class ResizeObserver extends OriginalResizeObserver {
  constructor(callback) {
    super((entries, observer) => {
      // Use requestAnimationFrame to prevent the "loop completed" error
      window.requestAnimationFrame(() => {
        callback(entries, observer);
      });
    });
  }
};

// Also suppress any remaining errors
const resizeObserverErr = window.onerror;
window.onerror = (message, source, lineno, colno, error) => {
  if (message && message.toString().includes('ResizeObserver')) {
    return true;
  }
  if (resizeObserverErr) {
    return resizeObserverErr(message, source, lineno, colno, error);
  }
  return false;
};

window.addEventListener('error', (event) => {
  if (event.message && event.message.includes('ResizeObserver')) {
    event.stopImmediatePropagation();
    event.preventDefault();
    return true;
  }
});

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  </React.StrictMode>,
);
